import { useSyncExternalStore } from 'react'

/**
 * The smallest thing that counts as routing.
 *
 * The app has one gate: signed out gets the marketing site, signed in gets the
 * workspace. That is still true. What it could not express is a page that
 * belongs to neither, and the guide is exactly that, so this adds a path to
 * read and a way to change it without a reload.
 *
 * Deliberately not a router. There is no route table, no params and no nested
 * outlets, because there is one public path and adding a dependency to express
 * `startsWith('/guide')` would be worse than writing it. The server already
 * falls unknown deep links back to `index.html`, so the URL works on a cold
 * load as well as a click.
 */

/**
 * `pushState` fires no event, so a click that changes the path would leave
 * every subscriber showing the old one. This is the event we fire ourselves;
 * `popstate` covers the back button, which the browser does announce.
 */
const NAVIGATED = 'stagepost:navigated'

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange)
  window.addEventListener(NAVIGATED, onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener(NAVIGATED, onChange)
  }
}

/** The current path, re-read whenever it changes. */
export function usePath(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => '/',
  )
}

/** Go somewhere in the app without reloading it. */
export function navigate(destination: string): void {
  window.history.pushState({}, '', destination)
  window.dispatchEvent(new Event(NAVIGATED))
}

/**
 * True for the kind of click that means "go there, in this tab".
 *
 * Everything else is someone asking the browser for something we should not
 * take away from them: a new tab, a new window, a download. The guide is a page
 * people will want open beside the app while they follow it, so cmd-click has
 * to keep working.
 */
export function isPlainLeftClick(event: {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  )
}
